import { Handle, NodeProps, Position } from 'reactflow';
import { Database, Server, HardDrive } from 'lucide-react';
import { PipelineNodeData } from '../../types/pipeline';

export interface TieredDestinationNodeData extends PipelineNodeData {
  config?: {
    destinationType?: 'redis' | 'postgresql' | 'clickhouse' | 'database';
    tier?: 'hot' | 'warm' | 'cold';
    keyPattern?: string;
    ttl?: number;
    partitionBy?: string;
    aggregations?: Array<{
      name: string;
      type: string;
      field: string;
      window: string;
    }>;
  };
}

const DESTINATION_CONFIG = {
  redis: {
    icon: Server,
    color: 'red',
    label: 'HOT Store',
    description: 'Redis - Real-time KPI',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    selectedRing: 'ring-red-200',
    iconColor: 'text-red-600',
    badgeColor: 'bg-red-100 text-red-700',
  },
  postgresql: {
    icon: Database,
    color: 'blue',
    label: 'WARM Store',
    description: 'PostgreSQL - Operational',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    selectedRing: 'ring-blue-200',
    iconColor: 'text-blue-600',
    badgeColor: 'bg-blue-100 text-blue-700',
  },
  database: {
    icon: Database,
    color: 'blue',
    label: 'WARM Store',
    description: 'Database - Operational',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    selectedRing: 'ring-blue-200',
    iconColor: 'text-blue-600',
    badgeColor: 'bg-blue-100 text-blue-700',
  },
  clickhouse: {
    icon: HardDrive,
    color: 'cyan',
    label: 'COLD Store',
    description: 'ClickHouse - Archive',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    selectedRing: 'ring-cyan-200',
    iconColor: 'text-cyan-600',
    badgeColor: 'bg-cyan-100 text-cyan-700',
  },
};

export default function TieredDestinationNode({ data, selected }: NodeProps<TieredDestinationNodeData>) {
  const destType = data.config?.destinationType || 'postgresql';
  const config = DESTINATION_CONFIG[destType];

  const IconComponent = config.icon;

  // Extract tier from label or config
  const tier = data.config?.tier || (destType === 'redis' ? 'hot' : destType === 'clickhouse' ? 'cold' : 'warm');

  return (
    <div
      className={`rounded-lg border px-3 py-2 shadow-sm min-w-[180px] ${
        selected ? `${config.borderColor} ring-2 ${config.selectedRing}` : config.borderColor
      } ${config.bgColor}`}
    >
      <Handle
        type="target"
        position={Position.Left}
        className={`!bg-${config.color}-500 !w-3 !h-3 !border-2 !border-${config.color}-600`}
      />

      {/* Tier Badge */}
      <div className="flex justify-center mb-2">
        <span className={`text-xs font-bold px-2 py-0.5 rounded ${config.badgeColor}`}>
          {tier.toUpperCase()} {tier === 'hot' && '⚡'}
        </span>
      </div>

      {/* Icon and Label */}
      <div className={`flex items-center gap-2 text-sm font-semibold ${config.iconColor}`}>
        <IconComponent className="h-4 w-4" />
        <span className="truncate">{data.label}</span>
      </div>

      {data.description && (
        <p className="mt-1 text-xs text-slate-500">{data.description}</p>
      )}

      {/* Config Summary */}
      <div className="mt-2 pt-2 border-t border-slate-200">
        {destType === 'redis' && data.config?.ttl && (
          <div className="flex justify-between text-xs text-slate-600">
            <span>TTL:</span>
            <span className="font-mono">{data.config.ttl}s</span>
          </div>
        )}
        {data.config?.aggregations && data.config.aggregations.length > 0 && (
          <div className="text-xs text-slate-600">
            <span className="font-medium">Aggregations: </span>
            <span className="font-mono">{data.config.aggregations.length}</span>
          </div>
        )}
        {destType === 'clickhouse' && data.config?.partitionBy && (
          <div className="flex justify-between text-xs text-slate-600">
            <span>Partition:</span>
            <span className="font-mono text-xs">{data.config.partitionBy}</span>
          </div>
        )}
      </div>
    </div>
  );
}
